import { Link } from "react-router-dom";
import { Stethoscope, HeartPulse, Wallet, FileText, Sparkles } from "lucide-react";

const CRITERIA = [
    {
        icon: Stethoscope,
        title: "Medizinische Dringlichkeit",
        body: "Wie akut ist der Zustand der Taube? Braucht sie dringend Medikamente, tierärztliche Versorgung oder intensive Pflege?",
    },
    {
        icon: HeartPulse,
        title: "Rettungschance",
        body: "Besteht eine realistische Chance auf Stabilisierung oder ein lebenswertes weiteres Leben?",
    },
    {
        icon: Wallet,
        title: "Finanzielle Belastung",
        body: "Wie hoch sind die bisherigen oder zu erwartenden Kosten für Versorgung, Medikamente oder Tierarzt?",
    },
    {
        icon: FileText,
        title: "Nachweise & Dokumentation",
        body: "Wurden Bilder, Rechnungen oder tierärztliche Nachweise eingereicht?",
    },
    {
        icon: Sparkles,
        title: "Eigeninitiative",
        body: "Kümmert sich die Person aktiv um die Versorgung, Pflege oder Vermittlung der Taube?",
    },
];

export default function FundCriteria({ showLinkToTransparency = false }) {
    return (
        <section
            className="glass p-6 sm:p-8 border border-[#f0c674]/15"
            data-testid="fund-criteria"
        >
            <div className="flex items-center gap-3 mb-2">
                <span className="h-px w-8 bg-[#f0c674]/40" />
                <span className="label text-[#f0c674]">Transparenz</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl mb-4">
                Wie der Himmelsfond hilft
            </h2>
            <p className="text-[#cbd5e1] leading-relaxed mb-4 text-sm sm:text-base">
                Jeder Eintrag im Taubenhimmel unterstützt gleichzeitig lebende Stadttauben
                in Not. Damit die Hilfe fair, nachvollziehbar und transparent bleibt, werden
                eingereichte Fälle nach festen Kriterien geprüft. Der Fond soll dort helfen,
                wo Unterstützung dringend benötigt wird und realistisch etwas bewirken kann.
            </p>

            <div className="mt-8">
                <div className="text-xs font-mono uppercase tracking-widest text-[#94a3b8] mb-4">
                    Nach diesen Kriterien werden Fälle bewertet
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CRITERIA.map(({ icon: Icon, title, body }) => (
                        <div
                            key={title}
                            className="flex gap-3 p-4 rounded-md bg-white/[0.02] border border-white/5"
                        >
                            <Icon
                                size={18}
                                className="text-[#f0c674] flex-shrink-0 mt-0.5"
                                strokeWidth={1.5}
                            />
                            <div>
                                <div className="font-medium text-[#f8fafc] text-sm mb-1">
                                    {title}
                                </div>
                                <p className="text-xs text-[#94a3b8] leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-8 p-4 rounded-md bg-[#f0c674]/[0.05] border border-[#f0c674]/15">
                <div className="text-xs font-mono uppercase tracking-widest text-[#f0c674] mb-2">
                    Der Fond unterstützt bevorzugt
                </div>
                <ul className="grid grid-cols-2 gap-y-1 gap-x-4 text-sm text-[#f8fafc]/90">
                    <li className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[#f0c674]" />
                        Akute Notfälle
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[#f0c674]" />
                        Medizinische Versorgung
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[#f0c674]" />
                        Medikamente
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[#f0c674]" />
                        Notwendige Tierarztkosten
                    </li>
                </ul>
            </div>

            <p className="text-xs text-[#94a3b8] leading-relaxed mt-6 italic">
                Um möglichst vielen Tauben helfen zu können, werden Unterstützungen in der
                Regel bis zu einem bestimmten Höchstbetrag pro Fall gewährt. Die genaue Höhe
                richtet sich nach Dringlichkeit, verfügbarer Fondssumme und Art des Notfalls.
                {showLinkToTransparency ? (
                    <>
                        {" "}Jede Unterstützung wird dokumentiert und anonymisiert auf der{" "}
                        <Link
                            to="/himmelsfonds"
                            className="text-[#f0c674] underline-offset-4 hover:underline"
                        >
                            Transparenzseite
                        </Link>{" "}
                        veröffentlicht.
                    </>
                ) : (
                    " Jede Unterstützung wird dokumentiert und anonymisiert unten auf dieser Seite veröffentlicht."
                )}
            </p>
            <p className="text-sm text-[#f8fafc]/90 leading-relaxed mt-4">
                Unser Ziel: mit den vorhandenen Mitteln möglichst vielen Tauben konkrete
                Hilfe ermöglichen. 🕊️
            </p>
        </section>
    );
}
